import { type ShippingFeePayer, type ShippingMode } from './shippingTotals';

export interface ShippingPayloadInput {
  receiveMethod: 'delivery' | 'pickup' | 'vault';
  values: Record<string, unknown>;
}

/**
 * Lọc field vận chuyển trước khi gửi BE.
 *
 * BE bật `forbidNonWhitelisted` và bỏ qua dữ liệu vận chuyển của đơn không phải
 * giao hàng — gửi lên chỉ tạo rác. Chuỗi rỗng cũng bỏ để đơn không lưu ghi chú
 * trắng.
 */
export function buildShippingPayload({ receiveMethod, values }: ShippingPayloadInput): Record<string, unknown> {
  if (receiveMethod !== 'delivery') return {};

  const mode = values.shipping_mode as ShippingMode | undefined;
  if (!mode) return {};

  const payload: Record<string, unknown> = {
    shipping_mode: mode,
    shipping_fee_payer: (values.shipping_fee_payer as ShippingFeePayer) ?? 'seller',
    // Chế độ ĐVVC không còn ép 0: phí là số hãng vừa báo, đã ghi vào form ở
    // dạng KHOÁ (POSPage) và hiện read-only ở cột phải. Ép 0 ở đây sẽ làm COD
    // gửi BE lệch đúng bằng phí ship so với con số thu ngân đang nhìn.
    shipping_fee: Number(values.shipping_fee ?? 0),
    // Freeship: gửi RIÊNG, không trừ sẵn vào `shipping_fee`. Phí gốc phải tới
    // BE nguyên vẹn để đơn ghi được cửa hàng bỏ ra bao nhiêu; trừ sẵn ở đây là
    // xoá mất con số đó và làm `is_free_shipping` không phân biệt nổi "phí 0"
    // với "phí được CTKM gánh".
    shipping_discount: Number(values.shipping_discount ?? 0) || 0,
    prepaid_amount: Number(values.prepaid_amount ?? 0),
    debt_amount: Number(values.debt_amount ?? 0),
  };

  // Task 18: dịch vụ mang thuế suất cho dòng phí VC — CHỈ có nghĩa ở chế độ tự
  // vận chuyển (ĐVVC không sinh dòng dịch vụ, xem `buildPosMoneyInput`). Bỏ
  // qua khi `null`/`undefined`: BE bật `forbidNonWhitelisted`, gửi field rỗng
  // cho đơn không dùng tới chỉ tạo rác.
  if (mode === 'self' && values.shipping_service_id) {
    payload.shipping_service_id = values.shipping_service_id;
  }

  if (mode === 'carrier') {
    // Guard mọi field bằng `!== undefined` (thay vì gán vô điều kiện) — nhất
    // quán với package_length/width/height bên dưới và với docstring "không
    // gửi field rỗng" của file này. Guard cho `package_weight` còn tránh
    // `Number(undefined)` = `NaN` (serialize thành `null` khi gửi BE) — hôm
    // nay không sờ tới được vì cổng checkout đã bắt buộc `package_weight > 0`
    // trước khi cho tạo đơn, nhưng vẫn giữ guard để không phụ thuộc ngầm vào
    // luật đó.
    if (values.shipping_partner !== undefined) payload.shipping_partner = values.shipping_partner;
    if (values.shipping_label !== undefined) payload.shipping_label = values.shipping_label;
    if (values.package_weight !== undefined) payload.package_weight = Number(values.package_weight);
    if (values.package_length !== undefined) payload.package_length = Number(values.package_length);
    if (values.package_width !== undefined) payload.package_width = Number(values.package_width);
    if (values.package_height !== undefined) payload.package_height = Number(values.package_height);
    if (values.shipping_promo_code) payload.shipping_promo_code = values.shipping_promo_code;
    // Nơi lấy hàng: BE lưu vào `orders.shipping.pickup_location_id` để lúc đẩy
    // vận đơn không phải đoán lại từ chi nhánh.
    if (values.shipping_pickup_location_id) {
      payload.pickup_location_id = values.shipping_pickup_location_id;
    }

    // Luôn gửi tường minh: `0` (không tick) khác hẳn `undefined` (đơn cũ) ở BE.
    payload.shipping_insurance_value =
      values.shipping_insurance_enabled === true
        ? Number(values.shipping_insurance_value ?? 0)
        : 0;
  }

  if (values.shipping_note) payload.shipping_note = values.shipping_note;

  return payload;
}
